import React from 'react';

import { useDataAttributes } from '@lumapps/data-attributes';
import { TagList } from '@lumapps/folksonomy/components/TagList';
import { isTagzEnabled } from '@lumapps/folksonomy/ducks/selectors';
import { Tag as FolksonomyTags } from '@lumapps/folksonomy/types';
import { mdiTagOutline } from '@lumapps/lumx/icons';
import { useSelector } from '@lumapps/redux/react';
import { Tag } from '@lumapps/tags/types';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { DrawerSection, DrawerSectionProps } from '../DrawerSection';

export interface DrawerTagsSectionProps extends Omit<DrawerSectionProps, 'title'> {
    /** list of folksonomy tagz */
    folksonomy?: FolksonomyTags[];
    /** list of tags  */
    tags?: Tag[];
    /** section title */
    title?: DrawerSectionProps['title'];
    /** scope for tracking purposes */
    scope?: string;
}

/**
 * Displays a list of tags inside the context of a Drawer. It can be either folksonomy tags or just regular tags
 * @family Drawer
 * @param DrawerFolksonomySectionProps
 * @returns DrawerFolksonomySection
 */
export const DrawerTagsSection: React.FC<DrawerTagsSectionProps> = ({
    folksonomy,
    tags,
    scope = 'drawer-tags',
    ...props
}) => {
    const { translateKey, translateObject } = useTranslate();
    const { get } = useDataAttributes(scope);
    const hasTagzFF = useSelector(isTagzEnabled);
    let tagsToDisplay: FolksonomyTags[] = [];

    if (hasTagzFF && folksonomy?.length) {
        tagsToDisplay = folksonomy;
    }

    if (tags && tags.length > 0) {
        tagsToDisplay = tags.map((tag) => {
            return {
                id: tag.id || tag.uuid,
                label: translateObject(tag.name),
            } as FolksonomyTags;
        });
    }

    return tagsToDisplay.length > 0 ? (
        <DrawerSection
            icon={mdiTagOutline}
            {...props}
            title={props.title || translateKey(GLOBAL.TAGS)}
            wrapperProps={{ ...get({ element: 'section', action: 'tags' }), ...props.wrapperProps }}
        >
            <TagList tags={tagsToDisplay} isClickable={false} />
        </DrawerSection>
    ) : null;
};
